import React, { PropsWithChildren } from 'react';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import fluidGridColumns from '../../../theme/fluidGridColumns';
import { CardColumnSpanCalculator } from '../../CardGrid';
import { Grid, GridItem } from '../../Grid';
import SolidBackground from '../../SolidBackground';
import { Heading, Intro, NoBackground, StyledCardGrid } from './styles';

export type ProductCardGridProps = {
  compact?: boolean;
  displayBackground?: boolean;
  heading?: string;
  intro?: string;
} & ComponentProps;

const getDesktopColumnSpan = (
  index: number,
  totalCards: number,
  compact: boolean | undefined
): number => {
  const colSpanForSingleItemRow = 8;
  const colSpanForTwoItemRow = 5;
  const colSpanForThreeItemRow = 4;
  const compactColSpanForFourItemRow = 3;

  if (totalCards === 1) {
    return colSpanForSingleItemRow;
  }

  if (compact && totalCards === 4) {
    return compactColSpanForFourItemRow;
  }

  const totalCardsModuloThree = totalCards % 3;
  switch (totalCardsModuloThree) {
    case 0:
      // All rows are rows of three.
      return colSpanForThreeItemRow;
    case 1:
      // All rows except the last two are rows of three, the final two are rows of two.
      return index < totalCards - 4 ? colSpanForThreeItemRow : colSpanForTwoItemRow;
    case 2:
      // All rows except the last are rows of three, the final one is a row of two.
      return index < totalCards - 2 ? colSpanForThreeItemRow : colSpanForTwoItemRow;
    default:
      throw new Error(
        `Expected remainder of less than 3, but found ${totalCardsModuloThree}. Check implementation!`
      );
  }
};

export const columnSpanCalculator: (
  compact: boolean | undefined
) => CardColumnSpanCalculator = (compact: boolean | undefined) => (
  index: number,
  totalCards: number
) => ({
  // mobile cards are always full-width
  tabletPortrait:
    totalCards % 2 === 1 && index === 0
      ? fluidGridColumns.tabletPortrait
      : fluidGridColumns.tabletPortrait / 2,
  tabletLandscape: getDesktopColumnSpan(index, totalCards, compact),
  desktop: getDesktopColumnSpan(index, totalCards, compact),
});

const ProductCardsContainer: React.FC<PropsWithChildren<ProductCardGridProps>> = ({
  compact,
  displayBackground,
  heading,
  intro,
  children,
  ...props
}) => {
  const Background = displayBackground ? SolidBackground : NoBackground;
  const displayHeader = heading || intro;
  return (
    <Background>
      {displayHeader && (
        <Grid alignLeft>
          <GridItem focusable>
            {heading && <Heading>{heading}</Heading>}
            {intro && <Intro>{intro}</Intro>}
          </GridItem>
        </Grid>
      )}
      <StyledCardGrid
        {...componentProps(props)}
        columnSpanCalculator={columnSpanCalculator(compact)}>
        {children}
      </StyledCardGrid>
    </Background>
  );
};

export default applyComponentTheme(ProductCardsContainer, 'ProductCardsContainer');
